import { Component } from "@theme/component";
import {
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  onDocumentLoaded,
} from "@theme/utilities";
import { sectionRenderer } from "@theme/section-renderer";
import { morph } from "@theme/morph";
import { RecentlyViewed } from "@theme/recently-viewed-products";
import { DialogCloseEvent, DialogComponent } from "@theme/dialog";

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLInputElement} searchInputMobile - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ["searchInput", "predictiveSearchResults", "resetButton"];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest("dialog-component");
  }

  get searchInput() {
    if (window.innerWidth <= 768) {
      return this.refs.searchInputMobile;
    } else {
      return this.refs.searchInput;
    }
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener("keydown", this.#handleKeyboardShortcut, {
        signal,
      });
      dialog.addEventListener(
        DialogCloseEvent.eventName,
        this.#handleDialogClose,
        {
          signal,
        },
      );

      this.addEventListener("click", this.#handleModalClick, { signal });
    }

    this.setAccordionListener();

    onDocumentLoaded(() => {
      //this.resetSearch(false); // Pass false to avoid focusing the input
    });
  }

  setAccordionListener() {
    const details = this.querySelectorAll('details[data-details="search"]');
    details.forEach((element) => {
      const detail = /** @type {HTMLDetailsElement} */ (element);
      detail.addEventListener("toggle", function () {
        if (this.open) {
          details.forEach((element) => {
            const otherDetail = /** @type {HTMLDetailsElement} */ (element);
            if (otherDetail !== this && otherDetail.open) {
              otherDetail.open = false;
            }
          });
        }
      });
    });
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  onCategoryChange(event) {
    this.handleChangeCollection(event, "category", "collection");
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  onCollectionChange(event) {
    this.handleChangeCollection(event, "collection", "category");
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * Logic: keep collections of the other criteria (ex. collections if categories changed) enabled
   * if they have at least one match on the remaining selectable collections
   * @param {MouseEvent} event - The mouse event
   * @param {string} smartcriteriaLeading - The smartcriteria of the leading checkbox
   * @param {string} smartcriteriaFollowing - The smartcriteria of the following checkbox
   */
  handleChangeCollection(event, smartcriteriaLeading, smartcriteriaFollowing) {
    if (this.checkIsFormEmpty()) {
      return;
    }
    const target = /** @type {HTMLInputElement} */ (event.target);

    //List of list of children collection for every caetgroy.
    /** @type {string[][]} */
    let allChildrenCollectionsHandles = [];

    // Get the other checked checkboxes of the same smartcriteria (categories or collections)
    //Dovrei prendere tutte quelle non disabled?
    this.querySelectorAll(
      `input[type="checkbox"][data-smartcriteria="${smartcriteriaLeading}"]:checked`,
    ).forEach((checkbox) => {
      /** @type {HTMLInputElement} */
      const input = /** @type {HTMLInputElement} */ (checkbox);
      //console.log('get childrens from checked category', input.dataset.collectionhandle);
      const stringCollectionHandles = input.dataset.childrencollections;
      //console.log('stringCollectionHandles:', stringCollectionHandles);
      const childrenCollectionsHandles =
        stringCollectionHandles?.split(",") || [];
      allChildrenCollectionsHandles.push(childrenCollectionsHandles);
    });

    //console.log('Children Collections to check:', allChildrenCollectionsHandles);

    /** @type {string[]} */
    const collectionHandlesStillEnabled = [];
    // Now enable/disable collection based on the collected category children collections
    this.querySelectorAll(
      `input[type="checkbox"][data-smartcriteria="${smartcriteriaFollowing}"]`,
    ).forEach((checkbox) => {
      /** @type {HTMLInputElement} */
      const input = /** @type {HTMLInputElement} */ (checkbox);
      const stringCollectionHandles = input.dataset.childrencollections;
      const childrenCollectionsHandles =
        stringCollectionHandles?.split(",") || [];

      // Check if childrenCollectionsHandles contains at least one element from every array in allChildrenCollectionsHandles
      const isMatch =
        allChildrenCollectionsHandles.length === 0 ||
        //use every if we want AND between categories and AND between collections
        allChildrenCollectionsHandles.some(
          (categoryChildrenCollectionsHandles) =>
            categoryChildrenCollectionsHandles.some((handle) =>
              childrenCollectionsHandles.includes(handle),
            ),
        );

      //console.log('isMatch (some array has at least some match):', isMatch);
      if (isMatch) {
        input.disabled = false;
        collectionHandlesStillEnabled.push(...childrenCollectionsHandles);
      } else {
        input.disabled = true;
        input.checked = false;
      }
    });

    /* Don't disable other collections of the same criteria. I must be able to select all collections or all categories
        console.log('--- Now check if others must be disabled/enabled');
        // Now, disable the checkboxes of the leading criteria if they have no matching enabled children collections, based on the results
        this.querySelectorAll(
            `input[type="checkbox"][data-smartcriteria="${smartcriteriaLeading}"]:not(:checked):not(:disabled)`
        ).forEach((checkbox) => {
            /** @type {HTMLInputElement} */
    /*
            const input = /** @type {HTMLInputElement} */
    /* (checkbox);
            
            console.log('checking if disable:', input.dataset.collectionhandle);
            const stringCollectionHandles = input.dataset.childrencollections;
            const childrenCollectionsHandles = this.parseCollectionHandles(stringCollectionHandles);
            console.log('childrenCollectionsHandles:', childrenCollectionsHandles);
            const isMatch = childrenCollectionsHandles.some((id) =>
                collectionHandlesStillEnabled.includes(id)
            );
            console.log('isMatch (some childrenCollectionsHandles are still enabled):', isMatch);

            if (isMatch) {
                input.disabled = false;
            } else {
                input.disabled = true;
                input.checked = false;
            }
        });
        */

    this.checkShowSelectedDot();
  }

  /**
   * Get the children collections IDs (only number) from an array string
   * @param {string | undefined | null} dataString - The mouse event
   * @returns {string[]} Array of collection IDs
   */
  parseCollectionIds(dataString) {
    return JSON.parse(dataString || "[]").map(
      /** @param {string} id */ (id) => id.split("/").pop(),
    );
  }

  checkIsFormEmpty() {
    const checked = this.querySelectorAll(
      `input[type="checkbox"][name="collection"]:checked`,
    );

    /** @type {HTMLButtonElement | null } */
    const resetButton = this.querySelector('button[name="reset-search"]');
    /** @type {HTMLButtonElement | null } */
    const submitButton = this.querySelector('button[name="submit-search"]');

    if (resetButton && submitButton) {
      if (checked.length === 0) {
        resetButton.disabled = true;
        submitButton.disabled = true;

        //enable all checboxes
        this.querySelectorAll(
          `input[type="checkbox"][name="collection"]`,
        ).forEach((checkbox) => {
          const input = /** @type {HTMLInputElement} */ (checkbox);
          input.disabled = false;
        });

        this.hideSelectedDots();
        return true;
      } else {
        resetButton.disabled = false;
        submitButton.disabled = false;
        return false;
      }
    }
  }

  clearSelection() {
    this.querySelectorAll(`input[type="checkbox"][name="collection"]`).forEach(
      (checkbox) => {
        const input = /** @type {HTMLInputElement} */ (checkbox);
        input.checked = false;
        input.disabled = false;
      },
    );

    /** @type {HTMLButtonElement | null } */
    const resetButton = this.querySelector('button[name="reset-search"]');
    /** @type {HTMLButtonElement | null } */
    const submitButton = this.querySelector('button[name="submit-search"]');

    if (resetButton && submitButton) {
      resetButton.disabled = true;
      submitButton.disabled = true;
    }

    this.hideSelectedDots();
  }

  hideSelectedDots() {
    const dotElementCategories = this.querySelector(
      '.selected-dot[data-smartcriteria="category"]',
    );
    const dotElementCollections = this.querySelector(
      '.selected-dot[data-smartcriteria="collection"]',
    );
    if (
      dotElementCategories &&
      dotElementCategories.classList.contains("hidden") === false
    ) {
      dotElementCategories.classList.add("hidden");
    }
    if (
      dotElementCollections &&
      dotElementCollections.classList.contains("hidden") === false
    ) {
      dotElementCollections.classList.add("hidden");
    }
  }

  checkShowSelectedDot() {
    const hasSelectedCategories =
      this.querySelectorAll(
        `input[type="checkbox"][data-smartcriteria="category"]:checked`,
      ).length > 0;
    const hasSelectedCollections =
      this.querySelectorAll(
        `input[type="checkbox"][data-smartcriteria="collection"]:checked`,
      ).length > 0;

    const dotElementCategories = this.querySelector(
      '.selected-dot[data-smartcriteria="category"]',
    );
    const dotElementCollections = this.querySelector(
      '.selected-dot[data-smartcriteria="collection"]',
    );

    if (dotElementCategories) {
      if (hasSelectedCategories) {
        dotElementCategories.classList.remove("hidden");
      } else {
        dotElementCategories.classList.add("hidden");
      }
    }
    if (dotElementCollections) {
      if (hasSelectedCollections) {
        dotElementCollections.classList.remove("hidden");
      } else {
        dotElementCollections.classList.add("hidden");
      }
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  submitCollectionFilteredSearch(event) {
    event?.preventDefault();

    /** @type {string[]} */
    const categoriesToSearch = [];
    /** @type {string[]} */
    const collectionsToSearch = [];

    //Get checked categoris and collections
    this.getCheckedCategoryInputs().forEach((input) => {
      if (input.dataset.filtervalue) {
        categoriesToSearch.push(input.dataset.filtervalue);
      }
    });
    this.getCheckedCollectionInputs().forEach((input) => {
      if (input.dataset.filtervalue) {
        collectionsToSearch.push(input.dataset.filtervalue);
      }
    });

    const url = new URL(
      Theme.routes.collections_url + "/jewelry",
      location.origin,
    );

    //Build the url params, with filters

    for (const categoryHandle of categoriesToSearch) {
      url.searchParams.append("filter.p.m.cliente.category", categoryHandle);
    }
    for (const collectionHandle of collectionsToSearch) {
      url.searchParams.append(
        "filter.p.m.cliente.collection",
        collectionHandle,
      );
    }

    window.location.href = url.toString();
  }

  /**
   * Returns all checked category checkboxes as an array of HTMLInputElement
   * @returns {HTMLInputElement[]}
   */
  getCheckedCategoryInputs() {
    return Array.from(
      this.querySelectorAll(
        `input[type="checkbox"][name="collection"][data-smartcriteria="category"]:checked`,
      ),
    );
  }
  /**
   * Returns all checked category checkboxes as an array of HTMLInputElement
   * @returns {HTMLInputElement[]}
   */
  getCheckedCollectionInputs() {
    return Array.from(
      this.querySelectorAll(
        `input[type="checkbox"][name="collection"][data-smartcriteria="collection"]:checked`,
      ),
    );
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest("button") ||
      target.closest("a") ||
      target.closest("input");

    if (!isInteractiveElement && this.searchInput) {
      this.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === "k") {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        ".predictive-search-results__wrapper-queries, " +
          ".predictive-search-results__wrapper-products, " +
          ".predictive-search-results__list",
      ),
    );

    const allItems = containers
      .flatMap((container) => {
        if (
          container.classList.contains(
            "predictive-search-results__wrapper-products",
          )
        ) {
          return Array.from(
            container.querySelectorAll(".predictive-search-results__card"),
          );
        }
        return Array.from(
          container.querySelectorAll(
            '[ref="resultsItems[]"], .predictive-search-results__card',
          ),
        );
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return (
      this.#allResultsItems?.findIndex(
        (item) => item.getAttribute("aria-selected") === "true",
      ) ?? -1
    );
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove("keyboard-focus");
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute("aria-selected", "true");

        if (this.#isKeyboardNavigation) {
          item.classList.add("keyboard-focus");
        }
        item.scrollIntoView({
          behavior: prefersReducedMotion() ? "instant" : "smooth",
          block: "nearest",
        });
      } else {
        item.removeAttribute("aria-selected");
      }
    }
    this.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      this.#resetSearch();
      return;
    }

    if (
      //!this.#allResultsItems?.length ||
      event.key === "ArrowLeft" ||
      event.key === "ArrowRight"
    ) {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;
    switch (event.key) {
      case "ArrowDown":
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex =
          currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case "Tab":
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex =
            currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex =
            currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case "ArrowUp":
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex =
          currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case "Enter": {
        const singleResultContainer =
          this.refs.predictiveSearchResults.querySelector(
            "[data-single-result-url]",
          );
        if (
          singleResultContainer instanceof HTMLElement &&
          singleResultContainer.dataset.singleResultUrl
        ) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }
        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector("a")?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set("q", this.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } =
      this.refs;

    const allRecentlyViewedElements = [
      ...(recentlyViewedItems || []),
      ...(recentlyViewedTitle || []),
    ];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add("removing");

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }
    const { predictiveSearchResults } = this.refs;
    predictiveSearchResults.classList.remove("hidden");

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector(
        ".predictive-search-results__inner",
      );
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector(
        ".predictive-search-form__content",
      );
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set("q", searchTerm);
    url.searchParams.set("resources[limit_scope]", "each");

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set(
      "q",
      viewedProducts
        .map(/** @param {string} id */ (id) => `id:${id}`)
        .join(" OR "),
    );
    url.searchParams.set("resources[type]", "product");

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = "predictive-search-empty";

    this.#currentIndex = -1;
    searchInput.value = "";
    this.#hideResetButton();

    //hide result container
    predictiveSearchResults.classList.add("hidden");

    const abortController = this.#createAbortController();
    const emptySectionMarkup = await sectionRenderer.getSectionHTML(
      emptySectionId,
      false,
    );
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, "text/html")
      .querySelector(".predictive-search-empty-section");

    if (!parsedEmptySectionMarkup) {
      return;
      //throw new Error('No empty section markup found');
    }

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup =
        await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(
        recentlyViewedMarkup,
        "text/html",
      );
      const recentlyViewedProductsHtml =
        parsedRecentlyViewedMarkup.getElementById("predictive-search-products");
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute("ref", "recentlyViewedWrapper");
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector(
        "#predictive-search-products",
      );
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get("predictive-search-component")) {
  customElements.define(
    "predictive-search-component",
    PredictiveSearchComponent,
  );
}
